import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

export interface LinkDefinition {
  rel: string;
  href: string;
  id?: string;
  type?: string;
  sizes?: string;
  media?: string;
  crossorigin?: string;
  hreflang?: string;
  [prop: string]: string | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class LinkService {
  private readonly renderer: Renderer2;
  private readonly document: Document;

  constructor(rendererFactory: RendererFactory2, @Inject(DOCUMENT) document: any) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.document = document;
  }

  /**
   * Adds a new link tag to the document head
   */
  addTag(tag: LinkDefinition, forceCreation = false): HTMLLinkElement {
    const selector = this.getSelector(tag);
    const existing = (selector && (this.document.head.querySelector(selector) as HTMLLinkElement)) || null;

    if (existing && !forceCreation) {
      return this.updateTag(tag);
    }

    const link = this.renderer.createElement('link') as HTMLLinkElement;
    this.setLinkProperties(link, tag);
    this.renderer.appendChild(this.document.head, link);
    return link;
  }

  /**
   * Updates an existing link tag or creates a new one if not found
   */
  updateTag(tag: LinkDefinition): HTMLLinkElement {
    const selector = this.getSelector(tag);
    const link = (selector && (this.document.head.querySelector(selector) as HTMLLinkElement)) || null;

    if (link) {
      this.setLinkProperties(link, tag);
      return link;
    }

    return this.addTag(tag, true);
  }

  /**
   * Gets a link tag by rel and href combination or other attributes
   */
  getTag(attrSelector: string): HTMLLinkElement | null {
    if (!attrSelector) return null;
    return this.document.head.querySelector(`link[${attrSelector}]`) || null;
  }

  /**
   * Removes a link tag with the specified attributes
   */
  removeTag(attrSelector: string): void {
    const link = this.getTag(attrSelector);
    if (link) {
      this.renderer.removeChild(this.document.head, link);
    }
  }

  /**
   * Removes all link tags matching the selector
   */
  removeTagsBySelector(attrSelector: string): void {
    const links = this.document.head.querySelectorAll(attrSelector);
    links.forEach((link) => {
      this.renderer.removeChild(this.document.head, link);
    });
  }

  private getSelector(tag: LinkDefinition): string {
    // Primary selector uses rel and href
    if (tag.id) {
      return `link#${tag.id}`;
    }
    return `link[rel="${tag.rel}"][href="${tag.href}"]`;
  }

  private setLinkProperties(link: HTMLLinkElement, tag: LinkDefinition): void {
    Object.entries(tag).forEach(([key, value]) => {
      if (value !== undefined) {
        this.renderer.setAttribute(link, key, value);
      }
    });
  }
}
